import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Mail } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../ui/tooltip';

const SUPPORT_EMAIL = 'support@nordinsight.com';

interface ContactDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function ContactDialog({
    open,
    onOpenChange,
}: ContactDialogProps) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(SUPPORT_EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-sm border-white/10 bg-zinc-800 p-8"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <DialogHeader className="space-y-3">
                    <DialogTitle className="text-lg">
                        {t('facilities.onboarding.contactDialog.title')}
                    </DialogTitle>
                    <DialogDescription className="text-sm leading-relaxed">
                        {t('facilities.onboarding.contactDialog.description')}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-between rounded-md border border-white/10 bg-zinc-900 px-3 py-2">
                    <a
                        href={`mailto:${SUPPORT_EMAIL}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-emerald-400 hover:text-emerald-300"
                    >
                        <Mail className="h-4 w-4" aria-hidden="true" />
                        {SUPPORT_EMAIL}
                    </a>
                    <TooltipProvider delayDuration={300} disableHoverableContent>
                        <Tooltip>

                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
                                    onClick={handleCopy}
                                    aria-label={t('facilities.onboarding.contactDialog.copyEmail')}
                                >
                                    {copied ? (
                                        <Check className="text-emerald-400" />
                                    ) : (
                                        <Copy />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {copied
                                    ? t('facilities.onboarding.contactDialog.copied')
                                    : t('facilities.onboarding.contactDialog.copyEmail')}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </DialogContent>
        </Dialog>
    );
}
